/**
 * Naming rules for page variable keys, and the decision about when the key
 * should follow the label.
 *
 * Extracted from PageElementProperties so the behaviour that caused variables
 * to be saved as a single letter is testable without a DOM.
 */

/** Normalise a finished value into a storable variable name. */
export function toVariableName(label: string): string {
  return label
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .toLowerCase();
}

/**
 * Normalise while the user is still typing.
 *
 * Trailing underscores are kept, unlike toVariableName: stripping them would
 * delete the separator the moment someone types it, so "first_" would
 * collapse to "first" and the next character would join the previous word.
 */
export function toEditableVariableKey(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+/, '')
    .toLowerCase();
}

export interface KeySeedState {
  /** Current label text. */
  label: string;
  /** True once the user has edited the key field themselves. */
  keyTouched: boolean;
  /** True when the element already has a variable bound. */
  hasBoundVar: boolean;
  /** True for element types that capture a value. */
  isInputElement: boolean;
}

/**
 * Whether the variable key should mirror the label right now.
 *
 * The original condition was "the key is currently empty", evaluated on every
 * keystroke. That is true only before the first character, so typing "Name"
 * seeded and immediately persisted the key as "n" and never revisited it --
 * the variable was saved as its first letter.
 *
 * Mirroring instead continues for as long as the user has not taken the key
 * over and no variable exists yet, and persistence moves to blur.
 */
export function shouldMirrorLabelToKey(state: KeySeedState): boolean {
  return state.isInputElement && !state.keyTouched && !state.hasBoundVar;
}

export interface ReusableVariable {
  id: string;
  name: string;
  scope: string;
  flowId?: string;
  pageNodeId?: string;
}

/**
 * Find an existing page variable this element should bind to instead of
 * creating a duplicate.
 *
 * Deleting a page element does not delete its variable -- deliberately, since
 * rich-text mentions reference variables by GUID and can outlive the element
 * that created them. So a page routinely holds variables with no element
 * pointing at them, and re-adding a field with the same label used to try to
 * create a second variable of that name, which the database refuses.
 *
 * The match mirrors the uniqueness rule exactly -- Variable_page_name_key is
 * (flowId, pageNodeId, name) WHERE scope = 'PAGE'. Matching more loosely would
 * bind to another page's variable; matching more tightly would still collide.
 */
export function findReusablePageVariable(
  variables: ReusableVariable[],
  target: { name: string; flowId: string; pageNodeId: string },
): ReusableVariable | undefined {
  if (!target.name) return undefined;

  return variables.find(
    (variable) =>
      variable.scope === 'PAGE' &&
      variable.name === target.name &&
      variable.flowId === target.flowId &&
      variable.pageNodeId === target.pageNodeId,
  );
}
